import type { DettaglioPreventivo, SpesaAccessoriaLetta, TrasfertaLetta } from '../../api/preventivi';
import { formattaImporto, formattaNumero, formattaPercentuale } from '../../lib/formato';

/*  DA DOVE ESCE OGNI NUMERO DEL RIEPILOGO.

    Chiesto in riunione il 14/09/2026 — «popup informativi sulle voci di costo» — e
    deciso con Andrea il 20/09: si aprono con un clic e restano finché non si clicca
    fuori, così il numero si può leggere con calma e copiare.

    Perché serve: sotto ogni voce c'era già una riga di contorno («31,2 ore a 41,00 €
    l'ora»), e su metà delle voci non basta. «Trasferte 284,00 €, calcolata dai giorni e
    dai chilometri» non dice quali giorni, quanti viaggi, quanti pasti — e quando il
    cliente chiede perché la trasferta costa così, la risposta va ricostruita a mano da
    cinque campi sparsi in tre sezioni della scheda.

    ────────────────────────────────────────────────────────────────────────────────
    LA REGOLA DI QUESTO FILE, e vale la pena leggerla prima di aggiungerci dentro:

    **qui non si calcola niente.** Si prendono i numeri che il server ha già mandato e
    si scrivono in fila. Le uniche moltiplicazioni che compaiono sono quelle di cui il
    DTO porta TUTTI i pezzi — viaggi, chilometri e tariffa stanno tutti e tre in
    `TrasfertaLetta`, quindi «4 × 400 × 0,50» si può scrivere — e servono a mostrare il
    conto, non a rifarlo: il risultato che si stampa è sempre quello arrivato dal
    server, mai il prodotto ricalcolato qui.

    Non è pignoleria. `vw_ElencoPreventivi` è una copia delle formule del dominio scritta
    in SQL, ed è rimasta indietro due volte in due settimane — la prima sugli oneri «di
    cui», la seconda sui pasti in trasferta. Una terza copia dentro una spiegazione
    sarebbe peggio delle altre due: una griglia sbagliata la si vede confrontando due
    schermate, una spiegazione sbagliata è autorevole proprio perché spiega.
    ────────────────────────────────────────────────────────────────────────────────
*/

/** Una riga del conto: a sinistra cosa, a destra quanto. `quanto` manca sulle righe di testo. */
export interface RigaSpiegazione {
  cosa: string;
  quanto?: string;
}

export interface VoceRiepilogo {
  chiave: string;
  nome: string;
  /** La riga sotto il nome, sempre a schermo: il riassunto in sei parole. */
  dettaglio?: string;
  importo: number;
  /** Il conto per esteso, dietro il clic. */
  spiegazione: RigaSpiegazione[];
}

const quasiUguali = (a: number, b: number) => Math.abs(a - b) < 0.015;

/*  LE VOCI CHE SI SOMMANO, nell'ordine in cui si leggono.

    Le «di cui» non ci sono: sono già dentro il prezzo e si dichiarano a parte, sommarle
    qui vorrebbe dire contarle due volte. Il conto di questo elenco fa `costoTotale`, e
    c'è una prova che lo controlla — è la stessa somma che il 20/09 non tornava di 2.400 €
    perché i subappalti erano nel totale e non avevano una riga.  */
export function vociDelRiepilogo(p: DettaglioPreventivo): VoceRiepilogo[] {
  const c = p.conti;
  const voci: VoceRiepilogo[] = [];

  voci.push({
    chiave: 'materiali',
    nome: 'Materiali',
    importo: c.costoMateriali,
    spiegazione: [
      { cosa: 'Costo d’acquisto delle righe, quantità per prezzo.' },
      ...(c.costoSubappalti > 0
        ? [{ cosa: 'Le righe segnate come subappalto stanno nella voce sotto, non qui.' }]
        : []),
      ...(c.costoOpzionali > 0
        ? [
            {
              cosa: 'Le righe opzionali restano fuori: si aggiungono solo se il cliente le prende.',
              quanto: formattaImporto(c.costoOpzionali),
            },
          ]
        : []),
      { cosa: 'Materiali', quanto: formattaImporto(c.costoMateriali) },
    ],
  });

  /*  I SUBAPPALTI HANNO LA LORO RIGA, dal 20/09/2026.

      Erano dentro `costoTotale` e fuori dal riepilogo: le voci a schermo sommavano
      2.400 € meno del totale scritto sotto, e chi rifaceva il conto a mano non ci
      arrivava. È la terza volta che la separazione fra materiali e subappalti lascia
      indietro qualcosa — prima la vista dell'elenco, poi il budget di commessa, adesso
      questo — e tutte e tre le volte il difetto era lo stesso: qualcuno leggeva
      `costoMateriali` credendo che comprendesse tutto.  */
  if (c.costoSubappalti > 0) {
    voci.push({
      chiave: 'subappalti',
      nome: 'Subappalti',
      dettaglio: 'righe che fa un’impresa esterna',
      importo: c.costoSubappalti,
      spiegazione: [
        { cosa: 'Le righe con la spunta «questa voce la fa un altro».' },
        {
          cosa:
            'Stanno fuori dai materiali perché su un lavoro in cui metà del valore lo fa ' +
            'un’impresa esterna il margine si legge solo tenendoli separati. Il prezzo ' +
            'al cliente non cambia di un euro.',
        },
        { cosa: 'Subappalti', quanto: formattaImporto(c.costoSubappalti) },
      ],
    });
  }

  voci.push(voceManodopera(p));

  for (const voce of c.accessorie.filter((v) => v.nelTotale)) {
    voci.push(voceAccessoria(p, voce.nome, voce.importo, voce.calcolata));
  }

  if (p.costiGeneraliPercentuale > 0) {
    const base = c.costoDiretto + c.speseAccessorie;
    voci.push({
      chiave: 'generali',
      nome: 'Costi generali',
      dettaglio: `${formattaPercentuale(p.costiGeneraliPercentuale)} su costo diretto e accessorie`,
      importo: c.costiGenerali,
      spiegazione: [
        { cosa: 'Costo diretto (materiali, subappalti e manodopera)', quanto: formattaImporto(c.costoDiretto) },
        { cosa: 'Più le spese accessorie che si sommano', quanto: formattaImporto(c.speseAccessorie) },
        { cosa: 'Base', quanto: formattaImporto(base) },
        {
          cosa: `${formattaPercentuale(p.costiGeneraliPercentuale)} della base`,
          quanto: formattaImporto(c.costiGenerali),
        },
        { cosa: 'Si cambia in «Parametri e date», ed è la quota di struttura: ufficio, mezzi, assicurazioni.' },
      ],
    });
  }

  return voci;
}

/*  LA MANODOPERA, che è ore per tariffa finché nessuna riga ha la posa a corpo.

    Quando ce l'ha, il conto non torna più — quella riga costa l'importo fisso e le sue
    ore restano contate perché dicono quanto dura il cantiere — e la riga di contorno
    «31,2 ore a 41,00 € l'ora» diventa una bugia. Qui si dice invece di nasconderlo,
    **senza rifare il conto**: quanto farebbero le ore, quanto costa davvero, e la
    differenza. Da dove venga la differenza lo sa il dominio, non questa schermata.  */
function voceManodopera(p: DettaglioPreventivo): VoceRiepilogo {
  const c = p.conti;
  const tariffa = p.tariffaManodoperaOraria + c.supplementoOrario;

  /*  Con la trasferta dentro l'ora l'importo comprende la trasferta, che dall'elenco
      delle accessorie è sparita: è quello che è stato chiesto, «non la vedrai da nessuna
      parte». Il totale in fondo resta identico.  */
  const conTrasferta = c.supplementoOrario > 0;
  const importo = c.costoManodopera + (conTrasferta ? c.trasferta.importo : 0);
  const aOre = c.oreTotali * tariffa;

  const spiegazione: RigaSpiegazione[] = [
    { cosa: 'Ore di posa di tutte le righe incluse', quanto: `${formattaNumero(c.oreTotali)} ore` },
    { cosa: 'Tariffa oraria', quanto: `${formattaImporto(tariffa)} l'ora` },
  ];

  if (conTrasferta) {
    spiegazione.push({
      cosa: `La tariffa è ${formattaImporto(p.tariffaManodoperaOraria)} più ${formattaImporto(
        c.supplementoOrario,
      )} di trasferta spalmata sull'ora: la trasferta non compare fra le accessorie, sta qui dentro.`,
    });
  }

  if (!quasiUguali(importo, aOre)) {
    spiegazione.push({ cosa: 'Ore per tariffa farebbero', quanto: formattaImporto(aOre) });
    spiegazione.push({
      cosa:
        'Non torna perché qualche riga ha la posa a corpo: costa l’importo fisso che le è ' +
        'stato scritto, e le sue ore restano contate lo stesso perché dicono quanto dura ' +
        'il cantiere — da lì escono viaggi, indennità e pasti.',
    });
  }

  /*  La tariffa maggiorata si apre ANCHE sulla riga sempre a schermo, non solo dentro il
      popup. Era così da prima, e spostarla dietro un clic sarebbe stata una perdita: chi
      legge «44,15 € l'ora» su un preventivo dove la tariffa è 41 deve poter capire lo
      scarto senza chiedersi se qualcuno ha battuto un numero sbagliato.  */
  const conto = conTrasferta
    ? ` (${formattaImporto(p.tariffaManodoperaOraria)} più ${formattaImporto(
        c.supplementoOrario,
      )} di trasferta)`
    : '';

  return {
    chiave: 'manodopera',
    nome: 'Manodopera',
    dettaglio: `${formattaNumero(c.oreTotali)} ore a ${formattaImporto(tariffa)} l'ora${conto}`,
    importo,
    spiegazione: [...spiegazione, { cosa: 'Manodopera', quanto: formattaImporto(importo) }],
  };
}

/*  Una spesa accessoria. Le trasferte hanno il loro conto — sono l'unica voce che nasce
    da sei campi invece che da un importo scritto a mano — le altre dicono da dove viene
    l'importo: una percentuale su una base, o un numero battuto.  */
function voceAccessoria(
  p: DettaglioPreventivo,
  nome: string,
  importo: number,
  calcolata: boolean,
): VoceRiepilogo {
  if (calcolata) {
    return {
      chiave: `accessoria-${nome}`,
      nome,
      dettaglio: 'calcolata dai giorni e dai chilometri',
      importo,
      spiegazione: spiegaTrasferta(p.conti.trasferta),
    };
  }

  const spesa = p.spese.find((s) => s.nome === nome);
  return {
    chiave: `accessoria-${nome}`,
    nome,
    dettaglio: spesa === undefined ? undefined : descriviSpesa(spesa, p.conti.costoDiretto),
    importo,
    spiegazione: [
      ...(spesa?.descrizione ? [{ cosa: spesa.descrizione }] : []),
      ...(spesa?.percentuale != null
        ? [
            { cosa: 'Costo diretto', quanto: formattaImporto(p.conti.costoDiretto) },
            {
              cosa: `${formattaPercentuale(spesa.percentuale)} del costo diretto`,
              quanto: formattaImporto(importo),
            },
          ]
        : [{ cosa: 'Importo scritto a mano nella sezione «Spese accessorie».', quanto: formattaImporto(importo) }]),
    ],
  };
}

const descriviSpesa = (s: SpesaAccessoriaLetta, diretto: number) =>
  s.percentuale != null
    ? `${formattaPercentuale(s.percentuale)} su ${formattaImporto(diretto)} di costo diretto`
    : 'importo fisso';

/*  I PERNOTTAMENTI NON SONO LE NOTTI DEL CALENDARIO.

    `notti` sono notti-PERSONA: la camera si paga a testa, quindi due persone che
    dormono fuori una sera sola fanno due pernottamenti. Il conto era giusto e la parola
    no — «2 notti d'albergo» racconta due sere a chi ne sta pagando una, ed è la parola
    quella che il cliente controlla.

    Con una persona sola le due letture coincidono e la precisazione sarebbe rumore:
    esce solo quando la squadra è più di uno, che è l'unico caso in cui si sbagliava.  */
export function pernottamenti(t: TrasfertaLetta): string {
  const quanti = `${formattaNumero(t.notti)} ${t.notti === 1 ? 'pernottamento' : 'pernottamenti'}`;

  if (t.personeInSquadra <= 1) return quanti;

  const sere = t.notti / t.personeInSquadra;

  return `${quanti} (${formattaNumero(sere)} ${sere === 1 ? 'notte' : 'notti'} in ${formattaNumero(
    t.personeInSquadra,
  )})`;
}

/*  LA TRASFERTA, voce per voce.

    È la spiegazione che serviva di più: l'importo nasce da sei conti — viaggi,
    chilometri, indennità, pranzi, cene — e a schermo se ne vedeva uno solo, il totale.
    «Perché la trasferta costa 284 €» era una domanda a cui si rispondeva aprendo la
    sezione del cantiere e rifacendo i conti a mente.

    Le moltiplicazioni si scrivono perché il DTO porta tutti e tre i pezzi di ognuna; il
    risultato stampato resta quello del server.  */
export function spiegaTrasferta(t: TrasfertaLetta): RigaSpiegazione[] {
  const righe: RigaSpiegazione[] = [
    {
      cosa: `${formattaNumero(t.giorni)} giorni × ${formattaNumero(t.personeInSquadra)} persone`,
      quanto: `${formattaNumero(t.giornateUomo)} giornate uomo`,
    },
  ];

  if (t.kmAndataRitorno != null && t.costoKm > 0) {
    righe.push({
      cosa:
        `${formattaNumero(t.viaggi)} viaggi × ${formattaNumero(t.kmAndataRitorno)} km ` +
        `× ${formattaImporto(t.tariffaKm)} al km`,
      quanto: formattaImporto(t.costoKm),
    });
    if (t.viaggiCorretti) {
      righe.push({
        cosa: `I viaggi sono stati corretti a mano: il calcolo ne direbbe ${formattaNumero(
          t.viaggiCalcolati,
        )}.`,
      });
    }
  }

  if (t.costoIndennita > 0) {
    righe.push({
      cosa: `${formattaNumero(t.giornateUomo)} giornate × ${formattaImporto(
        t.indennitaGiornaliera,
      )} di indennità`,
      quanto: formattaImporto(t.costoIndennita),
    });
  }

  if (t.costoPasti > 0) {
    const pezzi: string[] = [];
    if (t.pranzi > 0) pezzi.push(`${formattaNumero(t.pranzi)} pranzi × ${formattaImporto(t.costoPranzo)}`);
    if (t.cene > 0) pezzi.push(`${formattaNumero(t.cene)} cene × ${formattaImporto(t.costoCena)}`);
    righe.push({ cosa: pezzi.join(' + '), quanto: formattaImporto(t.costoPasti) });
    righe.push({
      cosa:
        `Il cantiere dista ${formattaNumero(t.distanzaSolaAndata)} km dalla sede, sola andata: ` +
        'è quella la distanza con cui si leggono le soglie, non i chilometri del giro.',
    });
    if (t.pernottoDeciso) {
      righe.push({
        cosa: t.pernotta
          ? 'Il pernotto è stato deciso a mano: le cene ci sono anche se la distanza non le darebbe.'
          : 'Il pernotto è stato escluso a mano, quindi niente cene.',
      });
    }
  }

  /*  L'ALBERGO, che fino al 21/09/2026 non c'era: si dormiva fuori, si pagava la cena,
      e il letto non lo pagava nessuno. Riga sua e non dentro i pasti, perché è la voce
      che si va a cercare quando si controlla una trasferta lontana.  */
  if (t.costoAlbergo > 0) {
    righe.push({
      cosa: t.albergoForzato
        ? 'Albergo, importo scritto a mano'
        : `${pernottamenti(t)} × ${formattaImporto(t.costoPernottamento)} di albergo`,
      quanto: formattaImporto(t.costoAlbergo),
    });

    if (t.albergoForzato && t.costoAlbergoCalcolato !== t.costoAlbergo) {
      righe.push({
        cosa:
          `Il conto a notte direbbe ${formattaImporto(t.costoAlbergoCalcolato)}: ` +
          'capita quando la prenotazione è già stata fatta e si sa quanto è costata.',
      });
    }
  }

  if (t.importoForzato) {
    righe.push({
      cosa: `L'importo è stato scritto a mano: il calcolo qui sopra direbbe ${formattaImporto(
        t.importoCalcolato,
      )}.`,
    });
  }

  righe.push({ cosa: 'Trasferte', quanto: formattaImporto(t.importo) });
  return righe;
}

/*  Il costo totale e il prezzo: le due righe in fondo, che non sono voci ma somme. Hanno
    la loro spiegazione perché sono i due numeri che si guardano per primi.  */
export function spiegaCostoTotale(p: DettaglioPreventivo): RigaSpiegazione[] {
  return [
    ...vociDelRiepilogo(p).map((v) => ({ cosa: v.nome, quanto: formattaImporto(v.importo) })),
    { cosa: 'Costo totale', quanto: formattaImporto(p.conti.costoTotale) },
    ...(p.conti.diCuiEsposto > 0
      ? [
          {
            cosa:
              'Le voci «di cui» non sono in questa somma: sono già dentro il prezzo e si ' +
              'dichiarano a parte, sommarle qui vorrebbe dire contarle due volte.',
          },
        ]
      : []),
  ];
}

export function spiegaPrezzo(p: DettaglioPreventivo): RigaSpiegazione[] {
  const c = p.conti;
  const righe: RigaSpiegazione[] = [
    {
      cosa:
        'Il prezzo esce riga per riga: ognuna prende il ricarico suo, quello del suo ' +
        'capitolo o quello generale. Non è il costo totale più una percentuale.',
    },
    { cosa: 'Somma delle righe', quanto: formattaImporto(c.prezzoPieno) },
  ];

  if (c.aggiustamento !== 0) {
    righe.push({
      cosa: `${formattaPercentuale(p.aggiustamentoPercentuale)} su tutto il preventivo`,
      quanto: formattaImporto(c.aggiustamento),
    });
  }

  righe.push({ cosa: 'Prezzo', quanto: formattaImporto(c.prezzo) });
  righe.push({ cosa: 'Meno il costo totale', quanto: formattaImporto(-c.costoTotale) });
  righe.push({ cosa: 'Margine', quanto: formattaImporto(c.margine) });

  if (c.marginePercentuale != null) {
    righe.push({
      cosa: `${formattaPercentuale(c.marginePercentuale)} sul PREZZO e non sul costo: «quanto di quello che incasso mi resta».`,
    });
  }

  return righe;
}
